/* eslint-disable no-restricted-imports */
import React, {useState, useEffect, useCallback} from 'react'
import {CardEnum, CityImprovementTypes, PlayerColor} from '@colonist/game-states'
import {GameAction, PopupInformation, UserReportReasonEnum} from '@colonist/game-base-types'
import {UIGameManager} from '../../../../code/client/shared/scripts/ui_game/UIGameManager'
import {UIUtilsEjsVariables} from '../../../../code/client/shared/scripts/UIUtilsEjsVariables'
import {UIGameSocketProvider} from '../contexts/UIGameSocketContext'
import {UIGameLayout} from './UIGameLayout/UIGameLayout'
import {UIGameSpectatorsProvider} from './UIGameSpectators/UIGameSpectatorsContext'
import {GameStateEnums} from '../../../../code/shared/GameEnums'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {UIContextProvider} from '../contexts/UIContext'
import {UIGameMechanic} from '../../../../code/client/shared/scripts/ui_game/UIGameEventController'
import {GameStore} from '../store/gameStore'
import {GameStoreContext} from '../store/GameStoreContext'
import {UIDeviceController} from '../../../../code/client/shared/scripts/ui_html/UIDeviceController'
import {CardPopupInformation} from '@colonist/game-card-types'
import 'simplebar-react/dist/simplebar.min.css'

let globalResizeUIGame: undefined | VoidFunction
export function resizeUIGame() {
    if(globalResizeUIGame) globalResizeUIGame()
}

export interface UIGameOptions {
    isChatEnabled: boolean
    isAfterLiveGame?: boolean | undefined
    initialCanResign: boolean
}

export interface UIGameProps {
    gameManager: UIGameManager
    options: UIGameOptions
    gameStore: GameStore
}

export default function UIGame({
    gameManager,
    options,
    gameStore,
}: UIGameProps) {
    const isChatDefaultMinimized = gameManager.gameController.isBotGame()
    const isLandscapeMode = !UIUtilsEjsVariables.shouldDisplayNewMobileGameUI
    const orientation = UIUtilsEjsVariables.isUserOnMobileApp && !isLandscapeMode ? 'portrait' : 'landscape'

    const onSendChat = (message: string) => {
        gameManager.socketGameSend.submitChat(message)
    }

    const resize = () => {
        const canvas = gameManager.canvasController
        const gameContainer = document.getElementById('ui-game') as HTMLDivElement | null
        const view = canvas.app.view
        if(gameContainer) {
            gameContainer.style.top = view.getBoundingClientRect().top + 'px'
            gameContainer.style.left = view.style.left
            gameContainer.style.width = canvas.appW + 'px'
            gameContainer.style.height = canvas.appH + 'px'
        }
    }

    globalResizeUIGame = resize

    useEffect(() => {
        if(!UIUtilsEjsVariables.isUserOnMobileApp) {
            window.addEventListener('resize', resize)
        }
        resize()

        return () => {
            if(!UIUtilsEjsVariables.isUserOnMobileApp) {
                window.removeEventListener('resize', resize)
            }
        }
    }, [])

    const sendReportPlayer = useCallback((reportReason: UserReportReasonEnum, color: PlayerColor) => {
        gameManager.socketGameSend.reportPlayer(reportReason, color)
    }, [gameManager])

    const onSetChatVisible = useCallback((visible: boolean) => {
        gameManager.eventController.sendEvent(visible, UIGameEventEnums.ToggleChatFeed)
    }, [gameManager])

    const onSetLogVisible = useCallback((visible: boolean) => {
        gameManager.eventController.sendEvent(visible, UIGameEventEnums.ToggleLogFeed)
    }, [gameManager])

    const sendLeaveGame = useCallback(() => {
        gameManager.socketGameSend.leaveGame()
    }, [gameManager])

    const sendResignGame = useCallback(() => {
        gameManager.socketGameSend.resign()
    }, [gameManager])

    const sendDisableKarma = useCallback(() => {
        gameManager.socketGameSend.submitChat('/disablekarma')
    }, [gameManager])

    const sendForceOpenGuideBubble = useCallback(() => {
        gameManager.eventController.sendEvent(undefined, UIGameEventEnums.BeginnerModeForceOpenGuideBubble)
    }, [gameManager])

    const sendClickedPassTurn = useCallback(() => {
        gameManager.socketGameSend.clickedPassTurn()
    }, [gameManager])

    const sendRequestSpecialBuildPhase = useCallback(() => {
        gameManager.socketGameSend.requestSpecialBuildPhase()
    }, [gameManager])

    const sendCancelSpecialBuildPhase = useCallback(() => {
        gameManager.socketGameSend.cancelSpecialBuildPhase()
    }, [gameManager])

    const sendCancelAction = useCallback(() => {
        gameManager.socketGameSend.cancelAction()
    }, [gameManager])

    const sendBuildSettlement = useCallback(() => {
        gameManager.socketGameSend.buildSettlement()
    }, [gameManager])

    const sendBuildRoad = useCallback(() => {
        gameManager.socketGameSend.buildRoad()
    }, [gameManager])

    const sendBuildShip = useCallback(() => {
        gameManager.socketGameSend.buildShip()
    }, [gameManager])

    const sendMoveShip = useCallback(() => {
        gameManager.socketGameSend.moveShip()
    }, [gameManager])

    const sendClearRoadEdgeHighlight = useCallback(() => {
        gameManager.eventController.sendEvent([], UIGameEventEnums.SetRoadEdgeHighlight)
    }, [gameManager])

    const sendClearCornerHighlight = useCallback(() => {
        gameManager.eventController.sendEvent([], UIGameEventEnums.SetCornerHighlight)
    }, [gameManager])

    const sendClearTileHighlight = useCallback(() => {
        gameManager.eventController.sendEvent([], UIGameEventEnums.SetTileHighlight)
    }, [gameManager])

    const sendBuildCityWall = useCallback(() => {
        gameManager.socketGameSend.buildCityWall()
    }, [gameManager])

    const sendBuildCity = useCallback(() => {
        gameManager.socketGameSend.buildCity()
    }, [gameManager])

    const sendBuyDevCard = useCallback(() => {
        gameManager.socketGameSend.buyDevCard()
    }, [gameManager])

    const sendUpgradeKnight = useCallback(() => {
        gameManager.socketGameSend.upgradeKnight()
    }, [gameManager])

    const sendPlaceKnight = useCallback(() => {
        gameManager.socketGameSend.placeKnight()
    }, [gameManager])

    const sendTakeKnightAction = useCallback(() => {
        gameManager.socketGameSend.takeKnightAction()
    }, [gameManager])

    const sendActivateKnight = useCallback(() => {
        gameManager.socketGameSend.activateKnight()
    }, [gameManager])

    const sendConfirmCityUpgrade = useCallback((cityImprovementType: CityImprovementTypes) => {
        gameManager.socketGameSend.confirmCityUpgrade(cityImprovementType)
    }, [gameManager])

    const sendClickedDevelopmentCard = useCallback((cardEnum: CardEnum) => {
        gameManager.socketGameSend.clickedDevelopmentCard(cardEnum)
    }, [gameManager])

    const sendRequestActionSwap = useCallback((gameAction: GameAction) => {
        gameManager.socketGameSend.requestActionSwap(gameAction)
    }, [gameManager])

    const sendClickedDice = useCallback(() => {
        gameManager.socketGameSend.clickedDice()
    }, [gameManager])

    const sendConfirmUseDevelopmentCard = useCallback((popupInformation: CardPopupInformation | PopupInformation) => {
        gameManager.eventController.sendEvent(popupInformation, UIGameEventEnums.ConfirmUseDevelopmentCard)
    }, [gameManager])

    const sendDiceNumberAnimationEnd = useCallback((diceValue: number) => {
        gameManager.eventController.sendEvent(diceValue, UIGameEventEnums.DiceNumberAnimationEnd)
    }, [gameManager])

    const isUserOnDiscord = UIUtilsEjsVariables.isUserOnDiscord
    const isUserOnTablet = UIDeviceController.isUserOnTabletApp()

    // TODO remove when mobile end screen is reactjs
    const [tempMobileIsEndScreenVisible, setTempMobileIsEndScreenVisible] = useState(false)

    useEffect(() => {
        if(tempMobileIsEndScreenVisible === true) {
            onSetChatVisible(false)
            onSetLogVisible(false)
        }
    }, [tempMobileIsEndScreenVisible])

    const registerUIGameMechanic = useCallback((uiGameMechanic: UIGameMechanic) => gameManager.eventController.registerUIGameMechanic(uiGameMechanic),
        [gameManager])

    registerUIGameMechanic({
        onEvent: (data: any, eventType: GameStateEnums.UIGameEventEnums) => {
            switch(eventType) {
                case UIGameEventEnums.ReplayModeStart:
                case GameStateEnums.UIGameEventEnums.GameEndHideMap: setTempMobileIsEndScreenVisible(true); break
                case GameStateEnums.UIGameEventEnums.GameEndShowMap: setTempMobileIsEndScreenVisible(false); break
                default: break
            }
        },
    })

    return (
        <UIContextProvider isUserOnDiscord={isUserOnDiscord} isUserOnMobile={UIUtilsEjsVariables.isUserOnMobile} isUserOnTablet={isUserOnTablet}>
            <GameStoreContext.Provider value={gameStore}>
                <UIGameSocketProvider
                    registerUIGameMechanic={registerUIGameMechanic}
                    sendDisableKarma={sendDisableKarma}
                    sendLeaveGame={sendLeaveGame}
                    sendReportPlayer={sendReportPlayer}
                    sendResignGame={sendResignGame}
                    sendForceOpenGuideBubble={sendForceOpenGuideBubble}
                    sendClickedPassTurn={sendClickedPassTurn}
                    sendRequestSpecialBuildPhase={sendRequestSpecialBuildPhase}
                    sendCancelSpecialBuildPhase={sendCancelSpecialBuildPhase}
                    sendCancelAction={sendCancelAction}
                    sendBuildSettlement={sendBuildSettlement}
                    sendBuildRoad={sendBuildRoad}
                    sendBuildShip={sendBuildShip}
                    sendClearRoadEdgeHighlight={sendClearRoadEdgeHighlight}
                    sendClearCornerHighlight={sendClearCornerHighlight}
                    sendClearTileHighlight={sendClearTileHighlight}
                    sendBuildCityWall={sendBuildCityWall}
                    sendBuildCity={sendBuildCity}
                    sendBuyDevCard={sendBuyDevCard}
                    sendUpgradeKnight={sendUpgradeKnight}
                    sendPlaceKnight={sendPlaceKnight}
                    sendTakeKnightAction={sendTakeKnightAction}
                    sendActivateKnight={sendActivateKnight}
                    sendConfirmCityUpgrade={sendConfirmCityUpgrade}
                    sendMoveShip={sendMoveShip}
                    sendClickedDevelopmentCard={sendClickedDevelopmentCard}
                    sendRequestActionSwap={sendRequestActionSwap}
                    sendClickedDice={sendClickedDice}
                    sendConfirmUseDevelopmentCard={sendConfirmUseDevelopmentCard}
                    sendDiceNumberAnimationEnd={sendDiceNumberAnimationEnd}>
                    <UIGameSpectatorsProvider>
                        <UIGameLayout
                            initialCanResign={options.initialCanResign}
                            isChatDefaultMinimized={isChatDefaultMinimized}
                            isChatEnabled={options.isChatEnabled}
                            onSendChat={onSendChat}
                            onSetChatVisible={onSetChatVisible}
                            onSetLogVisible={onSetLogVisible}
                            orientation={orientation}/>
                    </UIGameSpectatorsProvider>
                </UIGameSocketProvider>
            </GameStoreContext.Provider>
        </UIContextProvider>
    )
}
